/**
 * Firebase app bootstrap and root React context.
 *
 * Initializes Firebase (Auth, Firestore with persistent cache, Functions, Storage, Analytics,
 * Performance, App Check). VITE_FIREBASE_ENV selects tenteams-dev vs production project config.
 *
 * Exposes auth, db, funcs, storage, analytics, user, authReady, and authLoading to all descendants.
 * Wrapped around the entire app in index.tsx.
 */
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { ensureCountryPickRatesSubscription } from '@/models/countryPickRates';
import { type Analytics, getAnalytics, setUserId } from 'firebase/analytics';
import { type User, getAuth, type Auth } from 'firebase/auth';
import { type FirebaseApp, initializeApp } from 'firebase/app';
import {
  type Firestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import { type Functions, getFunctions } from 'firebase/functions';
import { type FirebaseStorage, getStorage } from 'firebase/storage';
import { getPerformance } from 'firebase/performance';
import { ReCaptchaV3Provider, initializeAppCheck } from 'firebase/app-check';
import { useAuthState } from 'react-firebase-hooks/auth';

// Your web app"s Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
function getFirebaseConfig() {
  if (import.meta.env.VITE_FIREBASE_ENV === 'development') {
    return {
      apiKey: 'AIzaSyBMBCiIaFSAVkMNd2Vcd4iST3qmGAfjT2Q',
      authDomain: 'tenteams-dev.web.app',
      projectId: 'tenteams-dev',
      storageBucket: 'tenteams-dev.appspot.com',
      messagingSenderId: '232774450025',
      appId: '1:232774450025:web:848c3f568200d5c3f71bf8',
      measurementId: 'G-SFEVNPZJV8',
    };
  } else {
    return {
      apiKey: 'AIzaSyBcRu2I4ghF6mGWntbooEl72xhgeAhbKmc',
      authDomain: 'tenteams.co.uk',
      projectId: 'tenteams',
      storageBucket: 'tenteams.appspot.com',
      messagingSenderId: '882640012543',
      appId: '1:882640012543:web:cdf17372af252ef3c978ca',
      measurementId: 'G-RQH26KSPH5',
    };
  }
}

const firebaseApp = initializeApp(getFirebaseConfig());
const analytics = getAnalytics(firebaseApp);
const auth = getAuth(firebaseApp);
const db = initializeFirestore(firebaseApp, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
});
const functions = getFunctions(firebaseApp, 'europe-west2');
const storage = getStorage(firebaseApp);
if (import.meta.env.PROD) {
  // Initialize Performance Monitoring and get a reference to the service
  const perf = getPerformance(firebaseApp);
}
const appCheckKey =
  import.meta.env.VITE_FIREBASE_ENV === 'development'
    ? '6LeQZzwiAAAAAAWT8QWnOX7b1vYOeHpdOqSLXByH'
    : '6LcxGFIhAAAAAHWezhHUWzX8IkSZpCEWemCC3FdC';
initializeAppCheck(firebaseApp, {
  provider: new ReCaptchaV3Provider(appCheckKey),
  isTokenAutoRefreshEnabled: true,
});

export interface IFirebaseContext {
  app: FirebaseApp;
  auth: Auth;
  authLoading: boolean;
  /** True once Firebase Auth has resolved the initial session (signed in, or confirmed guest with null user). */
  authReady: boolean;
  db: Firestore;
  funcs: Functions;
  user: User | null;
  storage: FirebaseStorage;
  analytics: Analytics;
  /** Reload currentUser and re-read emailVerified (reload does not update context by itself). */
  refreshAuthUser: () => Promise<void>;
}

const startingContext: IFirebaseContext = {
  app: firebaseApp,
  auth: auth,
  authLoading: true,
  authReady: false,
  db: db,
  funcs: functions,
  user: auth.currentUser,
  storage: storage,
  analytics: analytics,
  refreshAuthUser: async () => {},
};

export const FirebaseContext = createContext(startingContext);

export function FirebaseProvider(props: PropsWithChildren) {
  const [hookUser, loading] = useAuthState(auth);
  const [authRevision, bumpAuthRevision] = useReducer((n: number) => n + 1, 0);

  const refreshAuthUser = useCallback(async () => {
    await auth.authStateReady();
    if (auth.currentUser) {
      await auth.currentUser.reload();
    }
    bumpAuthRevision();
  }, []);

  const value = useMemo<IFirebaseContext>(
    () => ({
      app: firebaseApp,
      auth,
      authLoading: loading,
      authReady: !loading,
      db,
      funcs: functions,
      user: auth.currentUser ?? hookUser ?? null,
      storage,
      analytics,
      refreshAuthUser,
    }),
    [hookUser, loading, authRevision, refreshAuthUser],
  );

  useEffect(() => {
    ensureCountryPickRatesSubscription(db);
  }, []);

  // GA4 user_id = Auth UID so BigQuery can join competing WC-26 players (ANALYTICS_PHASE2).
  useEffect(() => {
    const uid = hookUser?.uid ?? auth.currentUser?.uid ?? null;
    setUserId(analytics, uid);
  }, [hookUser]);

  return <FirebaseContext.Provider value={value}>{props.children}</FirebaseContext.Provider>;
}
